import { Injectable } from '@angular/core';
import { MenuComprador } from '../interfaces/menu-comprador.interface';
import {
  canonicalMenuRaw,
  normalizeMenuCompradorArray,
} from '../utils/menu-comprador-canonical.util';

/** Prefijo de clave en localStorage; debe coincidir con `getMenuCacheKey` en AuthService. */
export const MENU_CACHE_KEY_PREFIX = 'menuasteric::';

/**
 * Prefijo de clave en sessionStorage para marcar si ya se ejecutó el fallback del shell;
 * debe coincidir con `getFallbackExecutedCacheKey` en AuthService.
 */
export const FALLBACK_EXECUTED_KEY_PREFIX = 'fallbackExecuted::';

/** Envelope persistido en localStorage (nuevo formato con TTL). */
interface MenuCacheEnvelope {
  data: MenuComprador[];
  storedAt: number;
}

function isMenuCacheEnvelope(value: unknown): value is MenuCacheEnvelope {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const o = value as Record<string, unknown>;
  return Array.isArray(o['data']) && typeof o['storedAt'] === 'number';
}

function menuFromParsedRoot(parsed: unknown): MenuComprador[] | null {
  if (Array.isArray(parsed)) {
    return normalizeMenuCompradorArray(parsed);
  }
  if (parsed !== null && typeof parsed === 'object') {
    const menu = (parsed as Record<string, unknown>)['menu'];
    if (Array.isArray(menu)) {
      return normalizeMenuCompradorArray(menu);
    }
  }
  return null;
}

@Injectable({
  providedIn: 'root'
})
export class MenuCacheService {
  /** TTL del menú en caché (4 horas). */
  private static readonly TTL_MS = 4 * 60 * 60 * 1000;

  private puedeUsarLocalStorage(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.localStorage !== 'undefined'
    );
  }

  /**
   * Lee el menú bajo `clave` (p. ej. clave completa del componente con `-registro` si aplica).
   * `raw` es siempre `JSON.stringify(data)` para comparación SWR con la respuesta del API.
   * Formato legacy (array JSON en raíz): sin TTL. Formato envelope `{ data, storedAt }`: expira tras TTL.
   */
  public leer(
    clave: string | null
  ): { raw: string; data: MenuComprador[]} | null {
    if (!this.puedeUsarLocalStorage()) {
      return null;
    }
    if (!clave) {
      return null;
    }
    try {
      const rawStored = window.localStorage.getItem(clave);
      if (rawStored === null || rawStored === '') {
        return null;
      }
      const parsed = JSON.parse(rawStored) as unknown;
      const legacyOrApiMenu = menuFromParsedRoot(parsed);
      if (legacyOrApiMenu !== null) {
        return { raw: canonicalMenuRaw(legacyOrApiMenu), data: legacyOrApiMenu };
      }
      if (isMenuCacheEnvelope(parsed)) {
        if (Date.now() - parsed.storedAt > MenuCacheService.TTL_MS) {
          this.eliminarClave(clave);
          return null;
        }
        const data = normalizeMenuCompradorArray(parsed.data ?? []);
        return { raw: canonicalMenuRaw(data), data };
      }
      this.eliminarClave(clave);
      return null;
    } catch {
      this.eliminarClave(clave);
      return null;
    }
  }

  /**
   * Persiste el menú bajo `clave` (envelope con timestamp) y retorna el string serializado del **payload**
   * (`JSON.stringify(data)`) para comparación SWR con la respuesta HTTP.
   */
  public guardar(clave: string | null, data: MenuComprador[]): string {
    const payload = normalizeMenuCompradorArray(data ?? []);
    const canonicalRaw = canonicalMenuRaw(payload);
    if (!this.puedeUsarLocalStorage()) {
      return canonicalRaw;
    }
    if (!clave) {
      return canonicalRaw;
    }
    const envelope: MenuCacheEnvelope = {
      data: payload,
      storedAt: Date.now()
    };
    const rawStored = JSON.stringify(envelope);
    try {
      window.localStorage.setItem(clave, rawStored);
    } catch {
      // Quota exceeded / modo privado: la app funciona sin cache
    }
    return canonicalRaw;
  }

  /**
   * Primera entrada bajo `menuasteric::` con menú no vacío (fallback sin clave exacta).
   */
  public leerPrimeraEntradaValida(): {
    clave: string;
    raw: string;
    data: MenuComprador[];
  } | null {
    if (!this.puedeUsarLocalStorage()) {
      return null;
    }
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (!key?.startsWith(MENU_CACHE_KEY_PREFIX)) {
          continue;
        }
        const leido = this.leer(key);
        if (leido && leido.data.length > 0) {
          return { clave: key, raw: leido.raw, data: leido.data };
        }
      }
    } catch {
      return null;
    }
    return null;
  }

  /**
   * Elimina entradas obsoletas bajo el prefijo `menuasteric::` (p. ej. al cerrar sesión).
   */
  public limpiarTodas(): void {
    if (!this.puedeUsarLocalStorage()) {
      return;
    }
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(MENU_CACHE_KEY_PREFIX)) {
          keysToRemove.push(k);
        }
      }
      for (const k of keysToRemove) {
        window.localStorage.removeItem(k);
      }
    } catch {
      // no-op
    }
  }

  private eliminarClave(clave: string): void {
    if (!this.puedeUsarLocalStorage()) {
      return;
    }
    try {
      window.localStorage.removeItem(clave);
    } catch {
      // no-op
    }
  }
}
